import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { API } from "../../../api/API";
import { downloadFile, getData } from "../../../CommonElements/Functions/PostGetFun";
import CommonTableNew from "../../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import Chart from "react-google-charts";
import ReactApexChart from "react-apexcharts";
import { Image } from "../../../AbstractElements";
import { dynamicImage } from "../../../Utils";
import { toast } from "react-toastify";
import { isOwnRequest } from "../../../Utils/authUtils";
// import { AlertSuccess } from '../../../Utils/CommonComponents/Alerts'; // Commented out AlertSuccess import temporarily

const ProgressChart: React.FC<{
  data: {
    TotalOutlets: { Count: number; OutlettypeName: string; Coverage: number }[];
  };
}> = ({ data }) => {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      {data.TotalOutlets.map((outlet, index) => {
        const progressOptions = {
          series: [
            outlet.Count > 0 ? (outlet.Coverage / outlet.Count) * 100 : 0,
          ],
          chart: {
            type: "radialBar" as const,
          },
          plotOptions: {
            radialBar: {
              hollow: {
                size: "50%",
              },
              dataLabels: {
                name: {
                  show: false, // Hide name inside the chart
                },
                value: {
                  show: true,
                  fontSize: "16px",
                  formatter: () => `${outlet.Coverage}/${outlet.Count}`, // Show value as 8/10
                },
              },
            },
          },
          labels: [outlet.OutlettypeName],
        };

        return (
          <div key={index} style={{ margin: "0 10px", textAlign: "center" }}>
            <ReactApexChart
              options={progressOptions}
              series={progressOptions.series}
              type="radialBar"
              height={200}
            />
            <div style={{ marginTop: "10px", fontSize: "14px" }}>
              {outlet.OutlettypeName}
            </div>{" "}
            {/* Label below the chart */}
          </div>
        );
      })}
    </div>
  );
};

const AgendaPieChart: React.FC<{
  data: { AgendaDays: { Count: number; Agendacontroltype: string }[] };
}> = ({ data }) => {
  const chartData = [
    ["Agenda Type", "Count"],
    ...data.AgendaDays.map(
      (agenda: { Count: number; Agendacontroltype: string }) => [
        agenda.Agendacontroltype,
        agenda.Count,
      ]
    ),
  ];

  return (
    <div style={{ textAlign: "center" }}>
      <Chart
        chartType="PieChart"
        width="250px"
        height="200px"
        data={chartData}
        options={{
          pieHole: 0.6,
          legend: { position: "bottom" }, // Keep legend for color-wise identification
          pieSliceText: "none", // Remove percentage text on the chart
          slices: {
            0: { color: "#338bff" }, // Example color for the first slice
            1: { color: "#1c65d3" }, // Example color for the second slice
            2: { color: "#0800fc" }, // Example color for the third slice
            3: { color: "#85cffd" }, // Example color for the fourth slice
          },
        }}
      />
      <div style={{ marginTop: "10px", fontSize: "14px" }}>
        Agenda Distribution
      </div>{" "}
      {/* Added label below the chart */}
    </div>
  );
};

const VisitFrequencyChart: React.FC<{
  data: { TotalVisits: number; VisitsFq: number };
}> = ({ data }) => {
  const percentage = (data.TotalVisits / data.VisitsFq) * 100;

  const progressOptions = {
    series: [percentage],
    chart: {
      type: "radialBar" as const,
      offsetY: -20,
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        hollow: {
          size: "50%",
        },
        dataLabels: {
          name: {
            show: false, // Hide name inside the chart
          },
          value: {
            show: true,
            fontSize: "16px",
            formatter: () => `${data.TotalVisits}/${data.VisitsFq}`, // Show value as 6/9
          },
        },
        track: {
          background: "#f2dede", // Light red background for the track
        },
      },
    },
    fill: {
      colors: ["#e11c15ff"], // Red fill color
    },
    labels: ["Visit Frequency"],
  };

  return (
    <div style={{ textAlign: "center" }}>
      <ReactApexChart
        options={progressOptions}
        series={progressOptions.series}
        type="radialBar"
        height={200}
      />
      <div style={{ marginTop: "10px", fontSize: "14px" }}>
        VisitsFq: {data.VisitsFq}
      </div>
    </div>
  );
};

type MTPChartsProps = {
  data: {
    employee: string;
    TotalOutlets: { Count: number; OutlettypeName: string; Coverage: number }[];
    AgendaDays: { Count: number; Agendacontroltype: string }[];
    TotalVisits: number;
    VisitsFq: number;
    Month: string;
    MtpStatus: string;
    MtpId?: number; // optional, if available
  }[];
  onAction: (action: "approve" | "reject", item: any) => void; // new prop
};

const MTPCharts: React.FC<MTPChartsProps> = ({ data, onAction }) => {
  if (!data || data.length === 0) {
    return <div>Not Any MTP Is In Requested Status</div>;
  }

  return (
    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
      {data.map((item, index: number) => {
        const formattedMonth = new Date(
          item.Month.replace(/-/g, "/")
        ).toLocaleString("default", { month: "short", year: "numeric" });
        // console.log(item.Month,'[[[[[[[[[[[[[[[');

        return (
          <div
            key={index}
            style={{
              marginBottom: "20px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
                borderBottom: "1px solid #ddd",
                padding: "7px",
              }}
            >
              <div style={{ fontWeight: "bold" }}>{`${item.employee}`}</div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span style={{ marginRight: "10px" }}>
                  {item.MtpStatus.charAt(0).toUpperCase() +
                    item.MtpStatus.slice(1)}
                </span>

                {!isOwnRequest(item) && (
                  <>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => onAction("approve", item)}
                    >
                      Approve
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => onAction("reject", item)}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </div>
            </div> 
                      
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                overflowX: "auto",
                paddingBottom: "10px",
              }}
            >
              <div style={{ flex: "0 0 auto", margin: "0 10px" }}>
                <ProgressChart data={item} />
              </div>
              <div style={{ flex: "0 0 auto", margin: "0 10px" }}>
                <AgendaPieChart data={item} />
              </div>
              <div style={{ flex: "0 0 auto", margin: "0 10px" }}>
                <VisitFrequencyChart data={item} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MTPApprovals: React.FC = () => {
  const [lastExpStats, setLastExpStats] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterMonth, setFilterMonth] = useState<string>(""); // Default selected month
  const [chartData, setChartData] = useState<any[]>([]); // New state for chart data
  const [modalOpen, setModalOpen] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject">("approve"); // Ensure proper type
  const [loader, setLoader] = useState(false);
  const [item, setItem] = useState<any>(null); // Ensure item is defined
  const navigate = useNavigate();
  const serverUrl = localStorage.getItem("serverUrl");
  const positionId = localStorage.getItem("positionId");
  const [searchText, setSearchText] = useState("");
  const [mtpStatusFilter, setMtpStatusFilter] = useState<string>(""); // Add state for MTP Status filter
  const [designationFilter, setDesignationFilter] = useState<string>(""); // Add state for Designation filter

  // Static values
  //   const position = 7405; // Example static position ID
  const position = positionId; // Example static position ID
  const canEdit = true; // Example static value for edit permission
  const formattedDate = "2025-08-20"; // Example static formatted date

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const url = `${serverUrl}${API.getTeamMtpStatus}`;
      const formattedFilterMonth = filterMonth
        ? `${String(new Date(filterMonth).getMonth() + 1).padStart(
            2,
            "0"
          )}-${new Date(filterMonth).getFullYear()}`
        : "";
      const { status, data } = await getData(url, true, {
        position_id: position,
        month: formattedFilterMonth,
        action: null,
      });

      if (status === 200 && Array.isArray(data?.data)) {
        // console.log("API.getTeamMtpStatus Response:", { status, data });
        setLastExpStats(data.data);
      } else {
        setLastExpStats([]);
      }
      setLoading(false);
    };

    if (filterMonth) {
      fetchData();
    }
  }, [filterMonth]);

  useEffect(() => {
    const fetchRequestedMTP = async () => {
      const url = `${serverUrl}${API.getRequestedMTPByPosition}`;
      const formattedFilterMonth = filterMonth
        ? `${String(new Date(filterMonth).getMonth() + 1).padStart(
            2,
            "0"
          )}-${new Date(filterMonth).getFullYear()}`
        : "";

      const { status, data } = await getData(url, true, {
        month: formattedFilterMonth,
      });
      // console.log("API.getRequestedMTPByPosition Response:", { status, data });

      if (status === 200 && Array.isArray(data?.data)) {
        setChartData(data.data); // Set the chart data state
      } else {
        setChartData([]); // Clear chart data if response is invalid
      }
      setLoading(false);
    };
    // setLoader(false);

    if (filterMonth) {
      fetchRequestedMTP();
    }
  }, [filterMonth]);

  const changeMtpStatus = async (action: "approve" | "reject") => {
    setLoader(true);
    setLoading(true);
    try {
      let result;
      const url = `${serverUrl}${
        action === "approve" ? API.approveMTP : API.rejectMTP
      }`;
      const params = { mtp_id: item?.MtpId };

      const { status, data } = await getData(url, true, params);

      if (status === 200) {
        toast.success(data.message || "MTP Status Updated Successfully!");
      } else {
        toast.error(data.message || "Failed to change MTP status.");
      }
    } catch (error) {
      console.error("Error changing MTP status:", error);
    } finally {
      setLoader(false);
      setLoading(false);
      setModalOpen(false);
    }
  };

  // Replaced MonthDropdown with a custom dropdown for months
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const currentMonth = new Date();
  const months = [
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 4, 1),
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 3, 1),
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 2, 1),
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1),
    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
  ];

  // Use a local YYYY-MM formatter to avoid timezone shifts caused by toISOString()
  const formatYYYYMM = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;


  const dropdownOptions = months
    .map(
      (date) =>
        [
          formatYYYYMM(date),
          date.toLocaleString("default", { month: "short", year: "numeric" }),
        ] as [string, string]
    )
    .sort((a, b) => b[0].localeCompare(a[0])); // Descending: latest month first

  const currentMonthKey = formatYYYYMM(currentMonth);
  const defaultSelectedKey =
    dropdownOptions.find(([key]) => key === currentMonthKey)?.[0] ??
    dropdownOptions[0]?.[0] ??
    "";

  const [selectedKey, setSelectedKey] = useState<string>(defaultSelectedKey);
  const handleSelect = (key: string) => {
    setSelectedKey(key);
    setLoading(true); // show loader immediately when user changes month
    // setLoader(true);
    setFilterMonth(key);
  };

  // Ensure the filterMonth is initialized to the default future month on mount
  React.useEffect(() => {
    if (!filterMonth && selectedKey) {
      setLoading(true); // show loader when initializing default month
      setFilterMonth(selectedKey);
    }
    // We intentionally run this only when selectedKey changes on first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const filteredData = lastExpStats.filter((row) => {
    const matchesSearchText = Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(searchText.toLowerCase())
    );
    const matchesDesignation = designationFilter
      ? row?.GradeName?.toLowerCase() === designationFilter.toLowerCase()
      : true;
    const matchesMtpStatus = mtpStatusFilter
      ? row?.MtpStatus?.toLowerCase() === mtpStatusFilter.toLowerCase()
      : true;
    return matchesSearchText && matchesDesignation && matchesMtpStatus;
  });

  const handleDownload = async () => {
    const formattedFilterMonth = filterMonth
      ? `${String(new Date(filterMonth).getMonth() + 1).padStart(2, "0")}-${new Date(filterMonth).getFullYear()}`
      : "";
    const url = `${serverUrl}${API.getTeamMtpStatus}`;
    const params = {
      position_id: position,
      month: formattedFilterMonth,
      action: "download",
    } as Record<string, any>;

    const res = await downloadFile(url, true, params);
    if (!res.ok) {
      toast.error(res.error || "Failed to download file");
    }
  };

  return (
    <div style={{ marginTop: "1px" }} className="px-2">
      <div className="d-flex justify-content-between align-items-center my-2 px-2">
        <h4 className="">
          <Col xs="auto">
            <Input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search here ..."
            />
          </Col>
        </h4>
        <div className="d-flex align-items-center gap-3">
          <Col xs="auto">
            <Input
              type="select"
              value={designationFilter}
              onChange={(e) => setDesignationFilter(e.target.value)}
              className="text-dark font-weight-bold"
              style={{ color: "#000", fontWeight: "bold" }}
            >
              <option value="" style={{ color: "#000" }}>All Designations</option>
              {Array.from(
                new Set(lastExpStats.map((row) => row?.GradeName))
              ).map((designation, index) => (
                <option key={index} value={designation} style={{ color: "#000" }}>
                  {designation}
                </option>
              ))}
            </Input>
          </Col>
          <Col xs="auto">
            <Input
              type="select"
              value={mtpStatusFilter}
              onChange={(e) => setMtpStatusFilter(e.target.value)}
              className="text-dark font-weight-bold"
              style={{ color: "#000", fontWeight: "bold" }}
            >
              <option value="" style={{ color: "#000" }}>All Statuses</option>
              {Array.from(
                new Set(lastExpStats.map((row) => row?.MtpStatus))
              ).map((status, index) => (
                <option key={index} value={status} style={{ color: "#000" }}>
                  {status}
                </option>
              ))}
            </Input>
          </Col>
          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle caret color="primary">
              {dropdownOptions.find(([key]) => key === selectedKey)?.[1] ||
                "Select Month"}
            </DropdownToggle>
            <DropdownMenu>
              {dropdownOptions.map(([key, label]) => (
                <DropdownItem key={key} onClick={() => handleSelect(key)}>
                  {label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button color="secondary" onClick={handleDownload} disabled={!filterMonth}>
            Download
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <div>
            <Image
              className="b-r-10 img-80"
              src={dynamicImage("dashboard/loader.gif")}
              alt="Loading..."
            />
          </div>
        </div>
      ) : (
        <>
          <CommonTableNew
            caption=""
            headData={[
              {
                id: "1",
                head: "Team Members",
                key: "EmployeeName",
                sortKey: "EmployeeName",
              },
              {
                id: "2",
                head: "Team Members",
                key: "EmployeeName",
                sortKey: "EmployeeName",
              },
              {
                id: "3",
                head: "Designation",
                key: "GradeName",
                sortKey: "GradeName",
              },
              {
                id: "4",
                head: "MTP Status",
                key: "MtpStatus",
                sortKey: "MtpStatus",
              },
              { id: "5", 
                head: "Action", 
                key: "Action" 
              },
            ]}
            data={filteredData} // Use filtered data here
            renderRow={(row, rowIndex) => (
              <tr key={rowIndex}>
                <td>{row?.EmployeeName || "-"}</td>
                <td>{row?.GradeName || "-"}</td>
                <td>
                  {row?.MtpStatus
                    ? row?.MtpStatus.charAt(0).toUpperCase() +
                      row?.MtpStatus.slice(1).toLowerCase()
                    : "-"}
                </td>
                <td>
                  {row?.MtpStatus?.toLowerCase() === "requested" ? (
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => {
                          navigate("/mtpcalendarbig", {
                            state: {
                              mtpStatus: row?.MtpStatus,
                              updated: filterMonth,
                              mtpId: row?.MtpId,
                              itemData: row,
                              backPath: "/Approvals",
                              backState: { activeTab: "1" },
                            },
                          });
                        }}
                      >
                        View
                      </Button>
                      {!isOwnRequest(row) && (
                        <>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => {
                              setActionType("approve");
                              setItem(row);
                              setModalOpen(true);
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => {
                              setActionType("reject");
                              setItem(row);
                              setModalOpen(true);
                            }}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    ["approved", "rejected"].includes(
                      row?.MtpStatus?.toLowerCase()
                    ) && (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => {
                          navigate("/mtpcalendarbig", {
                            state: {
                              mtpStatus: row?.MtpStatus,
                              updated: filterMonth,
                              mtpId: row?.MtpId,
                              backPath: "/Approvals",
                              backState: { activeTab: "1" },
                            },
                          });
                        }}
                      >
                        View
                      </Button>
                    )
                  )}
                </td>
              </tr>
            )}
          />

          {/* Second Table: Displaying chartData */}
          {/* <div style={{ marginTop: "20px" }}>
            <CommonTableNew
              caption=""
              headData={[
                {
                  id: "1",
                  head: "Employee",
                  key: "employee",
                  sortKey: "employee",
                },
                { id: "2", head: "Month", key: "Month", sortKey: "Month" },
                {
                  id: "3",
                  head: "MTP Status",
                  key: "MtpStatus",
                  sortKey: "MtpStatus",
                },
                {
                  id: "4",
                  head: "Total Visits",
                  key: "TotalVisits",
                  sortKey: "TotalVisits",
                },
                {
                  id: "5",
                  head: "Visit Frequency",
                  key: "VisitsFq",
                  sortKey: "VisitsFq",
                },
                // Dynamically add headers for TotalOutlets
                ...(chartData[0]?.TotalOutlets?.map(
                  (
                    outlet: {
                      Count: number;
                      OutlettypeName: string;
                      Coverage: number;
                    },
                    index: number
                  ) => ({
                    id: `outlet-${index}`,
                    head: outlet.OutlettypeName,
                    key: `outlet-${index}`,
                  })
                ) || []),
                // Dynamically add headers for AgendaDays
                ...(chartData[0]?.AgendaDays?.map(
                  (
                    agenda: { Count: number; Agendacontroltype: string },
                    index: number
                  ) => ({
                    id: `agenda-${index}`,
                    head: agenda.Agendacontroltype,
                    key: `agenda-${index}`,
                  })
                ) || []),
                { id: "6", head: "Actions", key: "actions" },
              ]}
              data={chartData}
              renderRow={(row, rowIndex) => (
                <tr key={rowIndex}>
                  <td>{row?.employee || "-"}</td>
                  <td>{row?.Month || "-"}</td>
                  <td>
                    {row?.MtpStatus
                      ? row?.MtpStatus.charAt(0).toUpperCase() +
                        row?.MtpStatus.slice(1).toLowerCase()
                      : "-"}
                  </td>
                  <td>{row?.TotalVisits || "-"}</td>
                  <td>{row?.VisitsFq || "-"}</td>
                  {row?.TotalOutlets?.map(
                    (
                      outlet: {
                        Count: number;
                        OutlettypeName: string;
                        Coverage: number;
                      },
                      index: number
                    ) => (
                      <td key={`outlet-${index}`}>
                        {outlet.Coverage}/{outlet.Count}
                      </td>
                    )
                  )}
                  {row?.AgendaDays?.map(
                    (
                      agenda: { Count: number; Agendacontroltype: string },
                      index: number
                    ) => (
                      <td key={`agenda-${index}`}>{agenda.Count}</td>
                    )
                  )}
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => {
                          setActionType("approve");
                          setItem(row);
                          setModalOpen(true);
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setActionType("reject");
                          setItem(row);
                          setModalOpen(true);
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
            />
          </div> */}
        </>
      )}

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          Confirm Action
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to {actionType} this MTP?</p>
          <div className="d-flex justify-content-end gap-2">
            <Button color="primary" onClick={() => changeMtpStatus(actionType)}>
              Yes
            </Button>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              No
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default MTPApprovals;
